import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

/**
 * Minimal typing for the PowerReviews 4.0 display API
 * (https://ui.powerreviews.com/stable/4.0/ui.js) -- only the surface the
 * sealy review blocks use.
 */
interface PowerReviewsRenderConfig {
    api_key: string;
    locale: string;
    merchant_group_id: string;
    merchant_id: string;
    page_id?: string;
    page_id_variant?: string;
    review_wrapper_url?: string;
    style_sheet?: string;
    components: Record<string, string>;
}

export interface PowerReviewsApi {
    display: {
        render(config: PowerReviewsRenderConfig): void;
    };
}

declare global {
    interface Window {
        POWERREVIEWS?: PowerReviewsApi;
    }
}

let _prLoading: Promise<PowerReviewsApi> | null = null;

// Load the Js library for the PowerReviews UI (mirrors utils/wistia.ts)
export const loadPowerReviews = async (): Promise<PowerReviewsApi> => {
    const document = getDocument();
    if (!document) {
        return Promise.reject(new Error("Document not available"));
    }
    if (!_prLoading) {
        const existing = getWindow()?.POWERREVIEWS;
        if (existing) {
            _prLoading = Promise.resolve(existing);
        } else {
            const s = document.createElement("script");
            s.type = "text/javascript";
            s.async = true;
            s.src = "https://ui.powerreviews.com/stable/4.0/ui.js";
            _prLoading = new Promise<PowerReviewsApi>((resolve, reject) => {
                s.onload = () => {
                    const pr = getWindow()?.POWERREVIEWS;
                    if (pr) {
                        resolve(pr);
                    } else {
                        reject(new Error("POWERREVIEWS failed to initialize"));
                    }
                };
                s.onerror = () => {
                    reject(new Error("PowerReviews script failed to load"));
                };
            }).catch((err: unknown) => {
                // Clear the memoized promise so a later call can retry after a
                // transient script-load/initialization failure, then re-throw
                // to keep the rejection observable to the caller.
                _prLoading = null;
                throw err;
            });
            document.body.appendChild(s);
        }
    }
    return _prLoading;
};
